/**
 * When a preorder has come close enough to be worked on - §9.643, owner O34.
 *
 * **Moved here, not written here.** This is `manager-orders/kitchen/elapsed.ts`'s own rule, which the kitchen
 * has asked on both of its lists since §9.318. The owner then asked for the same thing on the dispatch board -
 * *"Predobjednávka - nezobrazovať na rozvoz - nástenka - dokým nebude hodinu pred"* - and
 * `manager-delivery` does not depend on `manager-orders`, so the alternatives were a new module dependency or
 * a second copy of a rule about when food starts being made. §10A.275's precedent: a shared rule beats a
 * dependency added to reach one, and a second copy is `agos-one-guard-spelled-twice` on the screen a
 * dispatcher watches all day.
 *
 * **The threshold is the franchise's `kitchenDisplayInterval`, whose default is already 60 minutes** - so the
 * owner's *hodinu pred* is this product's own default and the board simply never asked for it.
 */

const MINUTE_MS = 60_000

/**
 * The franchise's lead time when it has not set one - §9.643.
 *
 * **60, read off `KitchenPage`'s own `DEFAULT_PREORDER_THRESHOLD_MINUTES` rather than chosen here.** Named in
 * one place so the board and the kitchen cannot default differently, which would put the same order on two
 * screens at two different times.
 */
export const DEFAULT_PREORDER_LEAD_MINUTES = 60

/**
 * Whether this preorder is within the franchise's lead time.
 *
 * <b>An order with no scheduled time is for now by definition</b>, which is why the absent case answers true
 * rather than false: every surface that filters on this shows ordinary orders too, and a false here would hide
 * all of them.
 *
 * `thresholdMinutes` is **required**. `boardOrders` takes its channels the same way for the same stated
 * reason - *"a fallback would silently reproduce the bug for any caller that forgot, and forgetting is the
 * whole failure mode"* - and a board that fell back to 60 while the franchise had set 15 would be wrong in the
 * quietest possible way.
 */
export function isPreorderDueNow(
  scheduledAt: string | null | undefined,
  thresholdMinutes: number,
  now: number,
): boolean {
  if (!scheduledAt) return true
  return minutesUntilScheduled(scheduledAt, now) <= thresholdMinutes
}

/** Whole minutes from `now` to `scheduledAt`, negative once it is past. */
export function minutesUntilScheduled(target: string, now: number): number {
  return Math.floor((new Date(target).getTime() - now) / MINUTE_MS)
}

/**
 * The franchise's lead time out of its settings blob, with the default filled in - §9.643.
 *
 * **Read here rather than at each screen.** `KitchenPage` had its own `readThreshold`, and the board needed
 * the same answer; two readers of one setting is how one of them comes to accept a negative or a blank the
 * other refuses. A value that is not a finite number at or above nought is the default, because a franchise
 * that has typed nonsense into a settings box has not chosen a lead time.
 */
export function preorderLeadMinutes(settings: unknown): number {
  const raw = (settings as { kitchenDisplayInterval?: unknown } | undefined)?.kitchenDisplayInterval
  const minutes = Number(raw)
  return Number.isFinite(minutes) && minutes >= 0 ? minutes : DEFAULT_PREORDER_LEAD_MINUTES
}
